import React, { useCallback } from "react";
import {
  HiOutlineChatAlt2,
  HiOutlineSearch,
  HiOutlineTrash,
} from "react-icons/hi";
import CustomButton from "components/common/Button/CustomButton";
import { classNames } from "Utils/utils";
import { useVertex360CopilotWorkspace } from "../hooks/useVertex360CopilotWorkspace";
import Vertex360CopilotConversationRow from "./Vertex360CopilotConversationRow";
import { Vertex360CopilotSidebarConversationsSkeleton } from "./Vertex360CopilotWorkspaceSkeleton";

const SCROLL_LOAD_THRESHOLD_PX = 72;

const LoadingChatsFooter = ({ visible }) => {
  if (!visible) return null;
  return (
    <div
      className="flex items-center justify-center gap-2 px-3 py-4 text-xs font-medium text-slate-500"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-[#4B6CB7]"
        aria-hidden="true"
      />
      <span>Loading chats…</span>
    </div>
  );
};

const Vertex360CopilotChatsManagement = () => {
  const {
    isLoadingConversations,
    filteredConversations,
    searchQuery,
    selectedIds,
    starredSection,
    recentSection,
    loadMoreStarredConversations,
    loadMoreRecentConversations,
    setSearchQuery,
    toggleSelectId,
    selectAllFiltered,
    clearSelection,
    requestDeleteSelectedConversations,
    selectConversation,
    starConversation,
    unstarConversation,
    renameConversation,
    requestDeleteConversation,
  } = useVertex360CopilotWorkspace();

  const isLoadingMore = Boolean(
    starredSection?.isLoadingMore || recentSection?.isLoadingMore
  );

  const onListScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (!el) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining > SCROLL_LOAD_THRESHOLD_PX) return;
      if (starredSection?.hasMore) loadMoreStarredConversations();
      if (recentSection?.hasMore) loadMoreRecentConversations();
    },
    [
      starredSection?.hasMore,
      recentSection?.hasMore,
      loadMoreStarredConversations,
      loadMoreRecentConversations,
    ]
  );

  const hasSelection = selectedIds.length > 0;
  const allSelected =
    filteredConversations.length > 0 &&
    filteredConversations.every((conversation) =>
      selectedIds.includes(conversation.id)
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#F8FAFE]">
      <div className="shrink-0 border-b border-slate-200/80 bg-white/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4B6CB7] via-[#3d5aa0] to-[#293F6A] text-white shadow-md shadow-[#4B6CB7]/20">
            <HiOutlineChatAlt2 className="h-4.5 w-4.5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight tracking-tight text-slate-900">
              Chats
            </h1>
            <p className="mt-0.5 text-xs leading-snug text-slate-500">
              Search, organise, and manage your Vertex360 Copilot conversations.
            </p>
          </div>
        </div>
      </div>

      <div
        className={classNames(
          "shrink-0 border-b px-4 py-3 transition-colors duration-200",
          hasSelection
            ? "border-[#C7D7F5] bg-[#F8FAFF]"
            : "border-slate-200/70 bg-white"
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {hasSelection
                ? `${selectedIds.length} selected`
                : "Manage conversations"}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {hasSelection
                ? "Delete or clear the selected chat conversations."
                : "Select chats to perform bulk actions."}
            </p>
          </div>

          {filteredConversations.length > 0 ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <CustomButton
                type="button"
                variant="secondary"
                label={allSelected ? "Deselect all" : "Select all"}
                clickHandler={() =>
                  allSelected ? clearSelection() : selectAllFiltered()
                }
                cssClass="!px-3 !py-2 !text-xs"
              />
              {hasSelection ? (
                <>
                  <CustomButton
                    type="button"
                    variant="danger"
                    label="Delete"
                    Icon={<HiOutlineTrash className="h-3.5 w-3.5" />}
                    clickHandler={requestDeleteSelectedConversations}
                    cssClass="!px-3 !py-2 !text-xs"
                  />
                  <CustomButton
                    type="button"
                    variant="secondary"
                    label="Cancel"
                    clickHandler={clearSelection}
                    cssClass="!px-3 !py-2 !text-xs"
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="relative mt-3">
          <HiOutlineSearch
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search chats by title..."
            className="w-full rounded-lg border border-slate-200 bg-slate-50/70 py-2.5 pl-10 pr-4 text-sm text-slate-800 shadow-inner shadow-slate-200/40 placeholder:text-slate-400 transition-all duration-200 focus:border-[#4B6CB7] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#4B6CB7]/15"
            aria-label="Search chats"
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-4 py-2">
        {isLoadingConversations ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white px-2 py-3 shadow-[0_10px_34px_-24px_rgba(41,63,106,0.45)]">
            <Vertex360CopilotSidebarConversationsSkeleton />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/80 px-4 text-center">
            <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ECF0FD] text-[#4B6CB7] ring-1 ring-[#C7D7F5]">
              <HiOutlineSearch className="h-5 w-5" aria-hidden />
            </span>
            <p className="text-sm font-semibold text-slate-700">No chats found</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-slate-400">
              {searchQuery.trim()
                ? "Try a different search term or clear the filter."
                : "Start a new chat from the sidebar to begin."}
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_10px_34px_-24px_rgba(41,63,106,0.45)]">
            <div
              className="v360-panel-scroll-gray min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto"
              onScroll={onListScroll}
            >
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={classNames(
                    "transition-colors duration-150",
                    selectedIds.includes(conversation.id) && "bg-[#F8FAFF]"
                  )}
                >
                  <Vertex360CopilotConversationRow
                    conversation={conversation}
                    showCheckbox
                    isSelected={selectedIds.includes(conversation.id)}
                    onOpen={selectConversation}
                    onToggleSelect={toggleSelectId}
                    onStar={starConversation}
                    onUnstar={unstarConversation}
                    onRename={renameConversation}
                    onDelete={requestDeleteConversation}
                  />
                </div>
              ))}
              <LoadingChatsFooter visible={isLoadingMore} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vertex360CopilotChatsManagement;
